import { QueryHookOptions, useApolloClient } from '@apollo/react-hooks'
import { wsClient } from 'graphql/client'
import { SessaoDocument, useLogoutMutation, useSessaoQuery } from 'graphql/hooks.generated'
import { AcessoCbo, Cbo, Lotacao, SessaoQuery, SessaoQueryVariables } from 'graphql/types.generated'
import { useCallback } from 'react'
import GruposCbo, { GrupoCbo } from 'types/GruposCbo'
import { Permission } from 'types/Permissions'
import { notify } from 'util/multitab'

export default (options: QueryHookOptions<SessaoQuery, SessaoQueryVariables> = {}) => {
  const client = useApolloClient()

  const { data, refetch, loading } = useSessaoQuery({
    fetchPolicy: 'cache-first',
    ...options,
  })

  const [logoutMutation] = useLogoutMutation({
    refetchQueries: [{ query: SessaoDocument }],
    awaitRefetchQueries: true,
  })

  const afterLogout = useCallback(async () => {
    await client.resetStore()
    wsClient.close(true)
    window.localStorage.removeItem('LOGIN')
    window.localStorage.removeItem('LOGOUT')
    window.localStorage.removeItem('PERFIL_SELECIONADO')
    window.localStorage.removeItem('RESET_SESSION')
  }, [client])

  const logout = useCallback(async () => {
    await logoutMutation()
    notify('LOGOUT')
    afterLogout()
  }, [logoutMutation, afterLogout])

  const hasAuthorization = useCallback(
    (permission: Permission) => {
      if (data?.sessao?.recursos) {
        return data.sessao.recursos.indexOf(permission.uri) !== -1
      } else {
        return false
      }
    },
    [data]
  )

  const isLotacao = (acesso): acesso is Lotacao => acesso.cbo

  const hasCboAuth = useCallback(
    (acessos: AcessoCbo[]) => {
      if (isLotacao(data?.sessao?.acesso)) {
        const sessionCbo: Cbo = data?.sessao?.acesso?.cbo
        const groups: GrupoCbo[] = GruposCbo.filter((grupoCbo: GrupoCbo) =>
          acessos.find((acesso) => acesso === grupoCbo.grupo)
        ).map((item) => item as GrupoCbo)

        return groups.some(
          (item: GrupoCbo) =>
            item.cbos.some((cbo) => sessionCbo.cbo2002 === cbo) ||
            item.cbos.some((cbo) => sessionCbo.cbo2002.startsWith(cbo))
        )
      } else {
        return false
      }
    },
    [data]
  )

  return {
    loading,
    data: data && data.sessao,
    logout,
    afterLogout,
    refresh: () => refetch(),
    hasAuthorization,
    hasCboAuth,
  }
}
